import { motion } from "motion/react";
import { Building2, GraduationCap, Users, Calendar, BarChart3, Globe2, ShieldCheck, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { ImageWithFallback } from "../components/figma/ImageWithFallback";

export function Schools() {
  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#0A1A2F] to-[#2E5C8A] py-8 md:py-12 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="inline-block bg-[#00FFFF] text-gray-900 px-4 py-1.5 rounded-full text-sm font-bold mb-8">
                FOR EDUCATIONAL INSTITUTIONS:
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-7xl mb-6 sm:mb-8 tracking-tight leading-tight">
                Bring World-Class <br />
                <span className="text-[#00FFFF]">STEM</span> to Your School
              </h1>
              <p className="text-lg sm:text-xl text-white/80 mb-8 sm:mb-10 leading-relaxed">
                ASTRA partners with leading international schools to deliver cutting-edge coding and robotics curriculums,
                teacher training, and fully-equipped innovation labs.
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4">
                <Button className="bg-[#FFD700] hover:bg-[#FFC700] text-[#0A1A2F] h-12 sm:h-14 px-6 sm:px-10 rounded-full font-bold text-base sm:text-lg">
                  Request Partnership Brochure
                </Button>
                <Button variant="outline" className="border-white/30 text-white h-12 sm:h-14 px-6 sm:px-10 rounded-full font-bold text-base sm:text-lg hover:bg-white/10">
                  Book a Presentation
                </Button>
              </div>
            </motion.div>
            
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-[40px] border border-white/20">
                <ImageWithFallback 
                  // src="https://images.unsplash.com/photo-1758685733633-a12889098460?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjaGlsZCUyMGNvZGluZyUyMG9uJTIwbGFwdG9wJTIwYnJpZ2h0JTIwY2xhc3Nyb29tfGVufDF8fHx8MTc3NjkzMzA4N3ww" 
                  src="Whisk_teenboybuildingrobot.jpeg"
                  alt="School classroom" 
                  className="rounded-[32px] w-full aspect-video object-cover"
                />
              </div>
              {/* Floating stats */}
              <div className="absolute -bottom-6 -left-2 sm:-bottom-10 sm:-left-10 bg-white text-gray-900 p-4 sm:p-8 rounded-2xl sm:rounded-3xl shadow-2xl flex items-center gap-4 sm:gap-6">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#5B9BD5] rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-black">4+</div>
                  <div className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-widest">Partner Schools</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services for Schools */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-20">
            <h2 className="text-3xl sm:text-4xl text-[#0A1A2F] mb-4 sm:mb-6">Our School Solutions</h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">We provide end-to-end support to help your institution lead in technology education.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                icon: <GraduationCap className="w-10 h-10" />,
                title: "Curriculum Licensing",
                desc: "Full access to our K-12 coding and robotics curriculum, aligned with international standards (CSTA, ISTE).",
                features: ["Lesson Plans", "Assessment Tools", "Student Workbooks"]
              },
              {
                icon: <Users className="w-10 h-10" />,
                title: "Teacher Empowerment",
                desc: "Intensive training programs and continuous professional development for your school's faculty.",
                features: ["On-site Training", "Webinars", "Technical Support"]
              },
              {
                icon: <Calendar className="w-10 h-10" />,
                title: "After-School Programs",
                desc: "We manage and run high-energy extracurricular clubs directly at your school campus.",
                features: ["Expert Instructors", "Equipment Provided", "Zero Admin for Schools"]
              }
            ].map((service, i) => (
              <div key={i} className="group p-6 sm:p-10 rounded-[32px] sm:rounded-[40px] bg-gray-50 hover:bg-white hover:shadow-2xl transition-all border border-transparent hover:border-gray-100">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#2E5C8A] rounded-2xl sm:rounded-3xl flex items-center justify-center text-white mb-6 sm:mb-8 group-hover:scale-110 transition-transform">
                  {service.icon}
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#0A1A2F] mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-8 leading-relaxed">{service.desc}</p>
                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-[#2E5C8A] font-bold">
                      <CheckCircle2 className="w-5 h-5 text-[#00FFFF]" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Partner with Us? */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-3xl sm:text-4xl text-[#0A1A2F] mb-6 sm:mb-8">The ASTRA Advantage</h2>
              <div className="space-y-6 sm:space-y-8">
                {[
                  { icon: <Globe2 />, title: "International Standards", desc: "Our curriculum meets global benchmarks for technology education." },
                  { icon: <BarChart3 />, title: "Measurable Impact", desc: "Detailed analytics on student progress and engagement levels." },
                  { icon: <ShieldCheck />, title: "Safe & Inclusive", desc: "Environmentally friendly equipment and pedagogy for all learners." }
                ].map((item, i) => (
                  <div key={i} className="flex gap-6">
                    <div className="w-12 h-12 bg-white rounded-xl shadow-md flex items-center justify-center flex-shrink-0 text-[#5B9BD5]">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-[#0A1A2F] mb-1">{item.title}</h4>
                      <p className="text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="order-1 lg:order-2 grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="aspect-square bg-[#FFD700] rounded-3xl overflow-hidden shadow-xl">
                  <ImageWithFallback src="Whisk_teacherandkid.jpeg" alt="Teacher and student" className="w-full h-full object-cover" />
                </div>
                <div className="bg-[#5B9BD5] p-8 rounded-3xl text-white">
                  <div className="text-4xl font-black mb-1">98%</div>
                  <div className="text-sm font-bold uppercase opacity-80">Parent Satisfaction</div>
                </div>
              </div>
              <div className="space-y-4 pt-12">
                <div className="bg-[#0A1A2F] p-8 rounded-3xl text-white">
                  <div className="text-4xl font-black mb-1">24/7</div>
                  <div className="text-sm font-bold uppercase opacity-80">Expert Support</div>
                </div>
                <div className="aspect-square bg-[#00FFFF] rounded-3xl overflow-hidden shadow-xl">
                  <ImageWithFallback src="/Whisk_boysinclass.jpeg" alt="Boys in class" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* School Contact CTA */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#E8F4FB] rounded-full flex items-center justify-center mx-auto mb-8 sm:mb-10">
            <Users className="w-8 h-8 sm:w-10 sm:h-10 text-[#2E5C8A]" />
          </div>
          <h2 className="text-3xl sm:text-4xl text-[#0A1A2F] mb-4 sm:mb-6">Let's Transform Your School Together</h2>
          <p className="text-lg sm:text-xl text-gray-600 mb-8 sm:mb-12">Our school consultants are ready to design a program that fits your specific needs and goals.</p>
          <Button className="w-full sm:w-auto bg-[#2E5C8A] hover:bg-[#5B9BD5] text-white h-14 sm:h-16 px-8 sm:px-12 rounded-full font-bold text-lg sm:text-xl shadow-2xl">
            Talk to a Consultant
          </Button>
        </div>
      </section>
    </div>
  );
}
